import type { BarAsset } from "../types/bar-asset";
import type { ProcessLine } from "../types/public-types";
import type { StrandLossOverlaySegment } from "./mapStrandLossOverlays";

const PROCESS_PADDING_FRACTION = 0.15;
const LINE_PADDING_FRACTION = 0.1;
const STRAND_CODES = new Set(["1", "2", "3", "4", "5"]);

export interface GanttBackgroundHit {
  asset: BarAsset;
  zone: "process" | "strand" | "delay" | "heating" | "other";
  x: number;
  y: number;
}

export const nearestRenderedDelay = (
  asset: BarAsset,
  x: number,
  y: number,
): ProcessLine | null => {
  let nearest: ProcessLine | null = null;
  let nearestDistance = Infinity;
  let nearestDelayId = Infinity;

  for (const task of asset.barTasks) {
    const line = task.source as ProcessLine;
    if (
      task.type !== "taskLine" ||
      line.eventCode !== "D" ||
      typeof line.DelayId !== "number" ||
      !Number.isFinite(line.DelayId) ||
      x < task.x1 ||
      x >= task.x2
    )
      continue;

    const distance = Math.max(task.y - y, 0, y - task.y - task.height);
    if (
      distance < nearestDistance ||
      (distance === nearestDistance && line.DelayId < nearestDelayId)
    ) {
      nearest = line;
      nearestDistance = distance;
      nearestDelayId = line.DelayId;
    }
  }
  return nearest;
};

export const nearestStrandLossSegment = (
  segments: readonly StrandLossOverlaySegment[],
  x: number,
  y: number,
): StrandLossOverlaySegment | null => {
  let nearest: StrandLossOverlaySegment | null = null;
  let nearestDistance = Infinity;

  for (const segment of segments) {
    if (x < segment.x || x >= segment.x + segment.width) continue;
    const distance = Math.max(segment.y - y, 0, y - segment.y - segment.height);
    if (
      distance < nearestDistance ||
      (distance === nearestDistance &&
        nearest !== null &&
        (segment.id < nearest.id ||
          (segment.id === nearest.id &&
            segment.loss.Strand < nearest.loss.Strand)))
    ) {
      nearest = segment;
      nearestDistance = distance;
    }
  }
  return nearest;
};

export const getGanttSvgPoint = (
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): { x: number; y: number } | null => {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  return new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
};

export const isGanttBackgroundTarget = (
  target: Element,
  svg: SVGSVGElement,
): boolean =>
  svg.contains(target) &&
  (target === svg || target.closest(".gridBody") !== null) &&
  target.closest(".content") === null;

export const getGanttBackgroundHit = (
  assets: BarAsset[],
  point: { x: number; y: number },
  svgWidth: number,
  defaultRowHeight: number,
  defaultBarFill: number,
  defaultLineSectionFill: number,
  speedBarBottomInset: number,
  strandLossSegments: readonly StrandLossOverlaySegment[] = [],
): GanttBackgroundHit | null => {
  if (
    !Number.isFinite(point.x) ||
    !Number.isFinite(point.y) ||
    point.x < 0 ||
    point.x >= svgWidth ||
    point.y < 0
  ) {
    return null;
  }

  let rowTop = 0;
  for (const asset of assets) {
    const rowHeight = asset.rowHeight || defaultRowHeight;
    if (point.y < rowTop + rowHeight) {
      const processHeight =
        (rowHeight * (asset.barFill ?? defaultBarFill)) / 150;
      const lineSectionHeight =
        (rowHeight * (asset.lineSectionFill ?? defaultLineSectionFill)) / 100;
      const processBottom =
        rowTop +
        (rowHeight - processHeight - lineSectionHeight) / 2 +
        processHeight;
      const lanes = asset.barTasks
        .filter((task) => task.type === "taskLine")
        .sort((a, b) => a.y - b.y);
      const firstEventStripe = lanes
        .filter(
          (task) =>
            STRAND_CODES.has(String(task.source.eventCode)) ||
            task.source.eventCode === "D" ||
            task.source.eventCode === "H",
        )
        .reduce(
          (first, task) =>
            Math.min(first, task.y + task.height - speedBarBottomInset),
          rowTop + rowHeight,
        );
      const padding = Math.min(
        processHeight * PROCESS_PADDING_FRACTION,
        lineSectionHeight * LINE_PADDING_FRACTION,
      );
      const processLimit = Math.min(
        rowTop + rowHeight,
        firstEventStripe,
        processBottom + padding,
      );

      if (point.y < processLimit) {
        return { asset, zone: "process", x: point.x, y: point.y };
      }

      const lane =
        lanes.find((task) => point.y < task.y + task.height) ??
        lanes[lanes.length - 1];
      const code = lane?.source.eventCode;
      let zone: GanttBackgroundHit["zone"] =
        code === "D"
          ? "delay"
          : code === "H"
            ? "heating"
            : STRAND_CODES.has(String(code))
              ? "strand"
              : "other";

      if (asset.name === "Caster") {
        const lossBottom = strandLossSegments
          .filter(
            (segment) =>
              segment.x <= point.x && point.x < segment.x + segment.width,
          )
          .reduce(
            (bottom, segment) => Math.max(bottom, segment.y + segment.height),
            -Infinity,
          );
        const delayTop = lanes
          .filter((task) => {
            const line = task.source as ProcessLine;
            return (
              line.eventCode === "D" &&
              typeof line.DelayId === "number" &&
              Number.isInteger(line.DelayId) &&
              line.DelayId > 0 &&
              task.x1 <= point.x &&
              point.x < task.x2
            );
          })
          .reduce((top, task) => Math.min(top, task.y), Infinity);

        if (
          Number.isFinite(lossBottom) &&
          Number.isFinite(delayTop) &&
          lossBottom < delayTop &&
          point.y >= lossBottom &&
          point.y < delayTop
        ) {
          zone = point.y < (lossBottom + delayTop) / 2 ? "strand" : "delay";
        } else if (!Number.isFinite(lossBottom) && Number.isFinite(delayTop)) {
          const lastStrandLaneBottom = lanes
            .filter((task) => STRAND_CODES.has(String(task.source.eventCode)))
            .reduce(
              (bottom, task) => Math.max(bottom, task.y + task.height),
              -Infinity,
            );
          if (
            Number.isFinite(lastStrandLaneBottom) &&
            lastStrandLaneBottom < delayTop &&
            point.y >= lastStrandLaneBottom &&
            point.y < delayTop
          ) {
            zone = "delay";
          }
        }
      }

      return {
        asset,
        zone,
        x: point.x,
        y: point.y,
      };
    }
    rowTop += rowHeight;
  }
  return null;
};
